import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { differenceInDays, isAfter, isBefore, parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MapPin, Calendar, CheckCircle2, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const JourneyTimeline = ({ startDate, endDate, tripName, destination, tripId }) => {
  const { token } = useAuth();
  const [coverUrl, setCoverUrl] = useState(null);
  const [loadingCover, setLoadingCover] = useState(false);

  // Fetch cover image
  useEffect(() => {
    const fetchCover = async () => {
      if (!tripId || !token) return;
      
      setLoadingCover(true);
      try {
        const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/cover`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.has_cover && data.cover_url) {
            setCoverUrl(data.cover_url);
          }
        }
      } catch (error) {
        console.log('No cover image available');
      } finally {
        setLoadingCover(false);
      }
    };

    fetchCover();
  }, [tripId, token]);

  const { progress, daysLeft, status, totalDays, currentDay } = useMemo(() => {
    const now = new Date();
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
    const total = differenceInDays(end, start) + 1;
    
    if (isBefore(now, start)) {
      const days = differenceInDays(start, now);
      return { progress: 0, daysLeft: days, status: 'upcoming', totalDays: total, currentDay: 0 };
    } else if (isAfter(now, end)) {
      return { progress: 100, daysLeft: 0, status: 'completed', totalDays: total, currentDay: total };
    } else {
      const elapsed = differenceInDays(now, start) + 1;
      const prog = Math.min(100, (elapsed / total) * 100);
      const remaining = differenceInDays(end, now);
      return { progress: prog, daysLeft: remaining, status: 'in_progress', totalDays: total, currentDay: elapsed };
    }
  }, [startDate, endDate]);

  const statusColors = {
    upcoming: { bg: 'bg-sky-500', text: 'text-sky-600', light: 'bg-sky-50 dark:bg-sky-900/30', darkText: 'dark:text-sky-400' },
    in_progress: { bg: 'bg-orange-500', text: 'text-orange-600', light: 'bg-orange-50 dark:bg-orange-900/30', darkText: 'dark:text-orange-400' },
    completed: { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50 dark:bg-green-900/30', darkText: 'dark:text-green-400' },
  };

  const colors = statusColors[status];

  return (
    <div className="relative rounded-2xl border border-slate-100 dark:border-slate-800 shadow-card overflow-hidden">
      {/* Background Cover Image */}
      {coverUrl && (
        <div className="absolute inset-0">
          <img 
            src={coverUrl} 
            alt="Trip cover" 
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
      )}
      
      {/* Content */}
      <div className={`relative p-6 ${coverUrl ? 'text-white' : 'bg-white dark:bg-slate-900'}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className={`font-heading font-bold text-lg ${coverUrl ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
              {tripName}
            </h3>
            <div className={`flex items-center gap-2 mt-1 ${coverUrl ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
              <MapPin className="w-4 h-4" />
              <span>{destination}</span>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            coverUrl 
              ? 'bg-white/20 backdrop-blur-sm text-white' 
              : `${colors.light} ${colors.text} ${colors.darkText}`
          }`}>
            {status === 'upcoming' && `Em ${daysLeft} dias`}
            {status === 'in_progress' && `Dia ${currentDay}/${totalDays}`}
            {status === 'completed' && 'Concluída'}
          </div>
        </div>

        {/* Journey Line */}
        <div className="relative">
          {/* Background Line */}
          <div className={`h-2 rounded-full overflow-hidden ${coverUrl ? 'bg-white/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
            <motion.div
              className={`h-full ${colors.bg} rounded-full`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>

          {/* Vehicle Icon */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2"
            initial={{ left: '0%' }}
            animate={{ left: `calc(${Math.min(progress, 95)}% - 16px)` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center shadow-lg`}>
              <span className="text-lg">✈️</span>
            </div>
          </motion.div>

          {/* Start & End Points */}
          <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-4 ${coverUrl ? 'border-white/50' : 'border-slate-300'}`} />
          <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-4 ${coverUrl ? 'border-white/50' : 'border-slate-300'}`}>
            {status === 'completed' && (
              <CheckCircle2 className="w-6 h-6 -ml-1 -mt-1 text-green-500" />
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="flex justify-between mt-4 text-sm">
          <div className={`flex items-center gap-1 ${coverUrl ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
            <Calendar className="w-4 h-4" />
            <span>{format(typeof startDate === 'string' ? parseISO(startDate) : startDate, "dd MMM", { locale: ptBR })}</span>
          </div>
          <div className={`flex items-center gap-1 ${coverUrl ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
            <Calendar className="w-4 h-4" />
            <span>{format(typeof endDate === 'string' ? parseISO(endDate) : endDate, "dd MMM", { locale: ptBR })}</span>
          </div>
        </div>

        {/* Status Message */}
        {status === 'upcoming' && (
          <p className={`mt-4 text-center font-accent text-lg ${coverUrl ? 'text-white/90' : 'text-slate-600 dark:text-slate-400'}`}>
            Faltam {daysLeft} dias para a aventura! 🎒
          </p>
        )}
        {status === 'in_progress' && (
          <p className={`mt-4 text-center font-accent text-lg ${coverUrl ? 'text-white' : 'text-orange-600'}`}>
            Aproveite cada momento! 🌴
          </p>
        )}
        {status === 'completed' && (
          <p className={`mt-4 text-center font-accent text-lg ${coverUrl ? 'text-white/90' : 'text-green-600'}`}>
            Que viagem incrível! 🎉
          </p>
        )}

        {/* Cover indicator badge */}
        {coverUrl && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white/80 text-xs flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            Capa
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneyTimeline;
